import { EdocsSortOption } from "constants/clinics";
import type { HearingLossThresholds } from "constants/hearing-loss";

import { PriceTables } from "./price-tables.type";
import { QuickBooksToken } from "./quickbooks.type";
import { Region } from "./regions.type";
import { StaffMember } from "./staff.type";

export interface Practice {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  tilled_account_id: string;
  justifi_account_id: string;
  card_processing_provider?: number;
  logo?: string;
  has_quickbooks_token: boolean;
  is_quickbooks_enabled: boolean;
  country: string;
  suno_comms_id?: string | null;
  scheduling_allow_overlap?: boolean;
  scheduling_ignore_room_reservation?: boolean;
  scheduling_should_create_schedule_for_staff?: boolean;
  scheduling_should_notify?: boolean;
  scheduling_staff_availability?: boolean;
  scheduling_cell_duration?: number;
  patient_settings: {
    required_fields: {
      referral_source: boolean;
      sex: boolean;
      sub_referral_source: boolean;
      birthdate: boolean;
    } | null;
  };
  hearing_loss_thresholds?: HearingLossThresholds | null;
  appointment_settings: {
    required_fields: {
      sub_referral_source: boolean | null;
    } | null;
  } | null;
  sale_lock_days?: number;
  unit_price_lock?: boolean;
  product_lock?: boolean;
  collect_outcome?: boolean;
  product_requests_deactivate_existing?: boolean;
  timezone?: string;
  is_messaging_enabled?: boolean;
  save_payment_card?: boolean;
  limit_to_current_clinic?: boolean;
  products_use_barcode_scanner?: boolean;
  go_live_at?: string;
  message_auto_read_enabled?: boolean;
  billing_use_practice_insurers_only?: boolean;
  create_all_sales_in_draft_status?: boolean;

  billing_available_payment_methods?: number[];
  use_room_traffic?: boolean;
  auto_create_patient_in_noah?: boolean;
  auto_create_physicians_from_intake?: boolean;
  use_unit_price_for_claims?: boolean;
  billing_default_payment_source_type?: number;
  box_default_folders?: string;
  edocs_sort_by?: EdocsSortOption;
  billing_allowed_payment_plan?: number;
  google_review_request_message_disabled?: boolean;
  send_appointment_reminder_on_friday?: boolean;
  default_appointment_reminder_duration?: string | null;
  private_feedback_request_disabled?: boolean;
  appointment_cancelled_message_disabled?: boolean;
  appointment_information_disabled?: boolean;
  no_show_disabled?: boolean;
  reschedule_disabled?: boolean;
  intake_form_request_reminder_disabled?: boolean;
  onboarding_form_reminder_duration?: string | null;
  create_bills_on_clinic_stock_checkin?: boolean;
  sendbird_chat_enabled?: boolean;
  post_insurance_payments_per_line_item?: boolean;
  generate_repair_form?: boolean;
  previous_oms?: number | null;
  use_apply_date_for_era?: boolean;
  enable_appointment_communications?: boolean;
  neon_origin_url?: string | null;
  neon_vendor_app_id?: string | null;
  neon_crm_app_id?: string | null;
  default_accept_assignment?: string | null;
  qbo_sync_refunds?: boolean;
  qbo_taxes_line_item_enabled_on?: string | null;
  allow_tilled_refund?: boolean;
  enforce_delivery_before_ready_to_bill?: boolean;
  stripe_customer_id?: string | null;
  include_draft_sales_in_revenue_calculations?: boolean;
  enable_processing_fee?: boolean;
  processing_fee_percent?: number;
  enable_darwill_statements_integration?: boolean;
  direct_mail_enabled?: boolean;
  enrolled_in_collections?: boolean;
  hide_global_tags?: boolean;
  force_sso_only?: boolean;
  is_claim_md_account_key_set?: boolean;
  migrated_appointment_notifications_disabled?: boolean;
  npi?: string;
  ein?: string;
  billing_name?: string;
  billing_street_address_1?: string;
  billing_street_address_2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_country?: string;
  billing_zip_code?: string;
  billing_phone?: string;
  manufacturer_filter_enabled?: boolean;
  patient_portal_documents?: boolean;
  patient_portal_reports?: boolean;
  patient_portal_videos?: boolean;
  enable_dashboard_widgets?: boolean;
}

export interface PracticeBillingSettings {
  practice_id?: number;
  credit_card_payment_enabled?: boolean;
  subscription_payment_enabled?: boolean;
  terminal_payment_enabled?: boolean;
  online_payment_enabled?: boolean;
  allow_payment_save?: boolean;
}

export type ClinicResponse = {
  id: number;
  name: string;
  practice: Practice;
  created_at: string;
  updated_at: string;
  tilled_account_id?: string;
  justifi_account_id?: string;
  fax_phone?: string;
  card_processing_provider?: number;
  type?: ClinicTypes;
  is_active: boolean;
  external_id: string;
  display_name: string;
  logo?: string | null;
  caption_call_referral_code?: string;
  five_keys_url?: string | null;
  business_name?: string;
  neon_origin_url?: string | null;
  neon_vendor_app_id?: string | null;
  neon_crm_app_id?: string | null;
};

export interface Clinic {
  id: number;
  name: string;
  display_name: string;
  fullName: string;
  practice: Practice;
  tilledAccountId?: string;
  justifiAccountId?: string;
  state?: string;
  street_address_1?: string;
  street_address_2?: string;
  zip_code?: string;
  city?: string;
  country?: string;
  npi?: string;
  phone?: string;
  fax_phone?: string;
  timezone?: string;
  created_at: string;
  updated_at: string;
  card_processing_provider?: number;
  noah_provider?: number | null;
  google_place_id?: string | null;
  scheduling_staff_availability?: boolean;
  is_active: boolean;
  external_id: string;
  type?: ClinicTypes;
  sms_phone?: string | null;
  users?: StaffMember[];
  price_tables?: ClinicPriceTable[];
  region?: Region;
  logo?: string | null;
  product_requests_deactivate_existing?: boolean;
  extra?: {
    id?: number;
    billng_city: string;
    billng_country: string;
    billng_phone: string;
    billng_state: string;
    billng_street_address_1: string;
    billng_street_address_2: string;
    billng_zip: string;
  } | null;
  box_folder_id?: string | null;
  noah_tenant_id?: string;
  ein?: string;
  caption_call_referral_code?: string;
  five_keys_url?: string | null;
  business_name?: string;
  neon_origin_url?: string | null;
  neon_vendor_app_id?: string | null;
  neon_crm_app_id?: string | null;
  is_claim_md_account_key_set?: boolean;
}

export interface ClinicPriceTable {
  clinic: number;
  price_table: PriceTables;
}

export type ClinicRequest = Omit<
  Clinic,
  | "users"
  | "created_at"
  | "id"
  | "updated_at"
  | "practice"
  | "fullName"
  | "price_tables"
  | "display_name"
> & {
  id?: number;
  quickbooks_token?: QuickBooksToken | null;
  users?:
    | { add: number[] }
    | { remove: number[] }
    | { add: number[]; remove: number[] };
  products?:
    | { add: number[] }
    | { remove: number[] }
    | { add: number[]; remove: number[] };
  price_tables?: number[];
  region?: number;
};

export type ClinicsResponse = Clinic[];

export interface ClinicFilters {
  search?: string;
  region?: number | number[] | null;
  type?: number | number[] | null;
  query?: string;
  user?: number;
  staff_member?: number;
  is_active?: boolean;
  qbo_company?: number;
}

export const enum ClinicTypes {
  FULL_TIME = 1,
  PART_TIME = 2,
  SERVICE_LOCATION = 3
}

export type TinyRegion = {
  id?: number;
  name: string;
};

export type TinyClinic = {
  id?: number;
  name: string;
  region?: TinyRegion;
};
